import { parseBulkCsv } from './bulkCsv';

export const MARGIN_HEADERS = ['product_barcode_id', 'product_name', 'sku', 'barcode', 'pack_quantity', 'unit', 'available_units', 'warehouse_id', 'destination_type', 'dispatch_to_id', 'margin_percent', 'proportion_factor', 'discount_percent', 'sales_ledger_id', 'is_active', 'notes'];
const text = value => String(value ?? '').trim();
const scopeKey = row => [text(row.product_barcode_id), text(row.destination_type) || 'all', text(row.dispatch_to_id), text(row.warehouse_id)].join('|');

export const parseMarginCsv = input => {
  // Read the header with the same delimiter and quoting rules as the CSV body.
  const probe = input.replace(/^\uFEFF/, '').replace(/^sep=([,;\t])\r?\n/i, '').split(/\r?\n/)[0];
  const headers = probe.split(/[,;\t]/).map(value => value.trim().replace(/^"|"$/g, '').toLowerCase().replace(/[\s-]+/g, '_'));
  if (!headers.includes('product_barcode_id') || !headers.includes('margin_percent')) throw new Error('Required columns: product_barcode_id, margin_percent. Use the margin CSV template or Inventory Stock margin export.');
  const unexpected = headers.filter(header => !MARGIN_HEADERS.includes(header));
  if (unexpected.length) throw new Error(`Unexpected columns: ${unexpected.join(', ')}.`);
  return parseBulkCsv(input, headers);
};

export const readMarginFile = async file => {
  if (file.size > 5 * 1024 * 1024) throw new Error('Upload a file below 5 MB.');
  if (/\.csv$/i.test(file.name)) return parseMarginCsv(await file.text());
  if (!/\.xlsx?$/i.test(file.name)) throw new Error('Upload an Excel (.xlsx or .xls) or CSV file.');
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellNF: true });
  const sheets = workbook.SheetNames.filter(name => workbook.Sheets[name]['!ref']);
  if (sheets.length !== 1) throw new Error('Use an Excel workbook with one non-empty sheet containing the margin table.');
  const sheet = workbook.Sheets[sheets[0]];
  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true, blankrows: false });
  // Excel percentage cells store 10% as 0.1; use displayed percentages as points.
  const range = XLSX.utils.decode_range(sheet['!ref']);
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })];
      if (cell?.f) throw new Error('Replace Excel formulas with values before uploading.');
    }
  }
  // Convert percentage formats before dropping blank rows.
  const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true, blankrows: true });
  rawRows.forEach((row, r) => row.forEach((value, c) => {
    const cell = sheet[XLSX.utils.encode_cell({ r: r + range.s.r, c: c + range.s.c })];
    if (typeof value === 'number' && cell?.z?.includes('%')) row[c] = `${value * 100}%`;
  }));
  if (!matrix.length) throw new Error('The workbook has no margin rows.');
  const csv = rawRows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  return parseMarginCsv(csv);
};

export const buildStockMarginRows = (products, getProductName) => {
  const grouped = new Map();
  products.forEach(item => {
    if (!item.product_barcode_id || item.is_active === false) return;
    const key = `${item.product_barcode_id}|${item.warehouse_id || ''}`;
    const units = Number(item.no_of_units || item.count_in_stock || 0);
    if (!Number.isFinite(units) || units <= 0) return;
    if (grouped.has(key)) { grouped.get(key).available_units += units; return; }
    grouped.set(key, {
      product_barcode_id: item.product_barcode_id, product_name: getProductName(item),
      sku: item.sku_id || '', barcode: item.bar_code || item.mk_barcode || '',
      pack_quantity: item.quantity ?? item.barcode_quantity ?? '', unit: item.unit_short_code || item.unit_name || '',
      available_units: units, warehouse_id: item.warehouse_id || '', destination_type: 'all', dispatch_to_id: '',
      margin_percent: '', proportion_factor: '', discount_percent: '', sales_ledger_id: '', is_active: '', notes: '',
    });
  });
  return [...grouped.values()];
};

export const planMarginImport = (rows, { productBarcodes, configurations, warehouses, outlets, stakeholders, ledgers }) => {
  const seen = new Set();
  const exists = (list, id) => list.some(item => text(item.id) === id);
  const numeric = (value, label, max = Infinity) => {
    const number = Number(text(value).replace(/%$/, ''));
    if (!text(value) || !Number.isFinite(number) || number < 0 || number > max) throw new Error(`${label} must be a number from 0 to ${max === Infinity ? 'a positive value' : max}.`);
    return number;
  };
  return rows.map((row, index) => {
    try {
      const productId = text(row.product_barcode_id);
      if (productId !== '*' && !exists(productBarcodes, productId)) throw new Error('Unknown product_barcode_id. Use the product pack ID, not the inventory batch ID.');
      const destination = text(row.destination_type).toLowerCase() || 'all';
      if (!['all', 'outlet', 'warehouse', 'internal_packing', 'vendor', 'customer'].includes(destination)) throw new Error('Invalid destination_type.');
      const destinationId = text(row.dispatch_to_id);
      const warehouseId = text(row.warehouse_id);
      if (warehouseId && !exists(warehouses, warehouseId)) throw new Error('Unknown warehouse_id.');
      if (destinationId) {
        const destinations = destination === 'outlet' ? outlets : destination === 'warehouse' ? warehouses
          : ['vendor', 'customer'].includes(destination) ? stakeholders.filter(item => text(item.stakeholder_type).toLowerCase().includes(destination)) : [];
        if (!exists(destinations, destinationId)) throw new Error('dispatch_to_id does not match destination_type.');
      }
      const scope = { product_barcode_id: productId, destination_type: destination, dispatch_to_id: destinationId, warehouse_id: warehouseId };
      const key = scopeKey(scope);
      if (seen.has(key)) throw new Error('Duplicate product pack and destination/warehouse scope. Combine into one row.');
      seen.add(key);
      const matches = configurations.filter(item => scopeKey(item) === key);
      if (matches.length > 1) throw new Error('Multiple existing configurations match this scope; resolve them before uploading.');
      const existing = matches[0];
      const payload = {
        ...scope, margin_percent: numeric(row.margin_percent, 'margin_percent'),
        proportion_factor: existing?.proportion_factor ?? 1, discount_percent: existing?.discount_percent ?? 0,
        sales_ledger_id: existing?.sales_ledger_id ?? '', is_active: existing?.is_active ?? true, notes: existing?.notes ?? '',
      };
      if (text(row.proportion_factor)) payload.proportion_factor = numeric(row.proportion_factor, 'proportion_factor');
      if (text(row.discount_percent)) payload.discount_percent = numeric(row.discount_percent, 'discount_percent', 100);
      if (text(row.sales_ledger_id)) {
        const value = text(row.sales_ledger_id);
        const idMatch = ledgers.find(ledger => text(ledger.id) === value);
        const matches = idMatch ? [idMatch] : ledgers.filter(ledger =>
          [ledger.ledger_name, ledger.ledger_code].some(label => text(label).toLowerCase() === value.toLowerCase()));
        if (!matches.length) throw new Error('Unknown sales_ledger_id. Use an existing ledger ID, name, or code.');
        if (matches.length > 1) throw new Error('Ambiguous sales_ledger_id. Use the numeric ledger ID from the ledger reference.');
        payload.sales_ledger_id = matches[0].id;
      }
      if (text(row.is_active)) {
        const active = text(row.is_active).toLowerCase();
        if (!['true', 'false', '1', '0'].includes(active)) throw new Error('is_active must be true or false.');
        payload.is_active = ['true', '1'].includes(active);
      }
      if (text(row.notes)) payload.notes = text(row.notes);
      return { row, payload, existingId: existing?.id };
    } catch (error) { throw new Error(`Row ${index + 2}: ${error.message}`); }
  });
};
